import React, { useMemo } from 'react';
import SEOHead from '../components/SEOHead';
import { getPlaceSchema, getFAQSchema } from '../utils/seoSchemas';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { citiesAPI, stadiumsAPI } from '../services/api';
import Map from '../components/Map';
import AdBanner from '../components/AdBanner';
import TravelLongformSections from '../components/TravelLongformSections';
import PrebuiltItineraryBlog from '../components/PrebuiltItineraryBlog';
import { getCityLongform } from '../content/travelLongform';
import { PREBUILT_ITINERARIES } from '../content/prebuiltItineraries/generated';
import { itineraryHubPath } from '../content/guides/itineraryHubMeta';

const CityGuide = () => {
  const { slug } = useParams();
  const { data: city, isLoading } = useQuery({
    queryKey: ['city', slug],
    queryFn: () => citiesAPI.getById(slug).then((r) => r.data),
    enabled: !!slug,
  });
  const { data: attractions = [] } = useQuery({
    queryKey: ['attractions', slug],
    queryFn: () => citiesAPI.getAttractions(slug).then((r) => r.data),
    enabled: !!slug,
  });
  const { data: tours = [] } = useQuery({
    queryKey: ['tours', slug],
    queryFn: () => citiesAPI.getTours(slug).then((r) => r.data),
    enabled: !!slug,
  });
  const { data: allStadiums = [] } = useQuery({
    queryKey: ['stadiums'],
    queryFn: () => stadiumsAPI.getAll().then((r) => r.data),
  });

  const stadiumsHere = useMemo(() => {
    if (!city) return [];
    return allStadiums.filter((s) => s.city === city.name || (city.id != null && s.city_id === city.id));
  }, [allStadiums, city]);

  const longform = useMemo(() => (city ? getCityLongform(city) : null), [city]);
  const prebuiltPlans = useMemo(() => (city?.slug ? PREBUILT_ITINERARIES[city.slug] : null), [city]);

  const cityFaq = useMemo(() => {
    if (!city) return [];
    const base = [
      {
        question: `Is ${city.name} a 2026 FIFA World Cup host city?`,
        answer: `${city.name} is featured in our planner as a North American host destination. Confirm which matches and fan events take place here via FIFA and the local host committee as the tournament approaches.`,
      },
      {
        question: `What should fans plan first when visiting ${city.name}?`,
        answer: `Lock in accommodation and internal transport early for match weeks. Use our ${city.name} attractions and tours below, then pair with the travel planner and itinerary tools for day-by-day planning.`,
      },
      {
        question: 'Where can I see World Cup fixtures involving this city?',
        answer: 'Browse the full match list and click through to each fixture for stadium maps, kickoff times, and links to venue guides.',
      },
    ];
    const extra = longform?.extraFaq?.length ? longform.extraFaq : [];
    return [...base, ...extra];
  }, [city, longform]);

  if (isLoading || !city) return <div className="p-8 text-center">Loading...</div>;

  const markers = city.latitude && city.longitude ? [{ lat: parseFloat(city.latitude), lng: parseFloat(city.longitude), label: city.name }] : [];
  const pageTitle = longform
    ? `${longform.seoTitleSuffix} | WorldCup Fan Planner`
    : `${city.name} Fan Guide – 2026 FIFA World Cup | WorldCup Fan Planner`;
  const pageDescription = longform?.metaDescription ||
    city.description ||
    `${city.name}, ${city.country}: travel tips, stadium access, attractions, and tours for fans attending the 2026 FIFA World Cup.`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        schema={[getPlaceSchema({ ...city, city: city.name }), getFAQSchema(cityFaq)]}
        ogType="article"
      />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {longform ? `${city.name} World Cup 2026 travel guide` : `${city.name}: 2026 FIFA World Cup fan guide`}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 flex items-center">
          <MapPin className="h-5 w-5 mr-2" />
          {city.country}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Last updated for planning purposes—verify event dates with{' '}
          <a href="https://www.fifa.com" className="text-amber-600 dark:text-amber-400 hover:underline" target="_blank" rel="noopener noreferrer">
            FIFA
          </a>{' '}
          and official city sources.
        </p>

        {city.description && <p className="mt-4 text-gray-700 dark:text-gray-300 leading-relaxed">{city.description}</p>}

        <p className="mt-4 text-gray-700 dark:text-gray-300 leading-relaxed">
          Planning a multi-city trip? Start with the{' '}
          <Link to="/travel-planner" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
            travel planner
          </Link>
          , explore the{' '}
          <Link to="/matches" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
            full match schedule
          </Link>
          , read our{' '}
          <Link to="/guides" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
            USA travel guides
          </Link>
          , and browse all{' '}
          <Link to="/cities" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
            host cities
          </Link>
          .
        </p>

        {longform && <TravelLongformSections data={longform} />}

        {prebuiltPlans ? (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
              Sample 3, 5, and 7-day itineraries
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Prefer a bookmarkable URL with extra context and FAQ? Open the{' '}
              <Link to={itineraryHubPath(city.slug)} className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
                {city.name} World Cup itinerary hub
              </Link>{' '}
              (same 3/5/7-day sample plans).
            </p>
            <PrebuiltItineraryBlog guideCityName={city.name} plansByDuration={prebuiltPlans} />
          </div>
        ) : null}

        {stadiumsHere.length > 0 && (
          <section className="mt-6 p-4 rounded-xl bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/50">
            <h2 className="font-semibold text-gray-900 dark:text-white mb-2">World Cup venues in this city</h2>
            <ul className="space-y-1">
              {stadiumsHere.map((s) => (
                <li key={s.id}>
                  <Link to={`/stadiums/${s.slug || s.id}`} className="text-amber-700 dark:text-amber-400 hover:underline font-medium">
                    {s.name}
                  </Link>
                  {s.capacity ? <span className="text-sm text-gray-600 dark:text-gray-400"> — capacity {Number(s.capacity).toLocaleString()}</span> : null}
                </li>
              ))}
            </ul>
          </section>
        )}

        {markers.length > 0 && (
          <div className="mt-6 h-64 rounded-lg overflow-hidden">
            <Map markers={markers} />
          </div>
        )}

        <AdBanner className="my-6" slot="city-page" />

        <div className="grid md:grid-cols-2 gap-8 mt-8">
          <div>
            <h2 className="text-xl font-bold mb-4">Top attractions</h2>
            <div className="space-y-2">
              {attractions.map((a) => (
                <div key={a.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="font-medium">{a.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {a.category} • {a.price} • {a.rating ? `★ ${a.rating}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-xl font-bold mb-4">Tours</h2>
            <div className="space-y-2">
              {tours.map((t) => (
                <div key={t.id} className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    ${t.price} • {t.provider}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <section className="mt-10 p-5 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700" aria-labelledby="city-faq">
          <h2 id="city-faq" className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {city.name} travel FAQ
          </h2>
          <dl className="space-y-4">
            {cityFaq.map(({ question, answer }) => (
              <div key={question}>
                <dt className="font-medium text-gray-900 dark:text-white">{question}</dt>
                <dd className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </>
  );
};

export default CityGuide;
